import type { Node, Relationship } from '@neo4j-nvl/base'
import { useRuntimeVariables } from 'app-kit'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { useCallback, useMemo, useRef } from 'react'

export interface GraphDataQuery {
  query: string
  params?: Record<string, unknown>
  organization_id: string
}

export type GraphRelationship = Relationship & {
  id: string
  start: string
  end: string
  properties: Record<string, string>
  captions: { value: string }[]
}
export type GraphNode = Node & {
  labels: string[]
  id: string
  properties: Record<string, string>
  captions: { value: string }[]
}

interface GraphDataResponse {
  results: Record<string, string | number>[]
  nodes?: GraphNode[]
  relationships?: GraphRelationship[]
  errors?: string[]
}

export interface GraphDataRequest {
  query: string
  params?: Record<string, unknown>
  organization_id: string
}

interface CacheEntry {
  response: GraphDataResponse
  timestamp: number
}

interface InFlightRequest {
  promise: Promise<GraphDataResponse>
  abortController: AbortController
}

const CACHE_TTL_MS = 5 * 60 * 1000
const MAX_CACHE_SIZE = 100

function generateCacheKey(request: GraphDataRequest): string {
  return JSON.stringify({
    query: request.query,
    params: request.params ?? {},
    organization_id: request.organization_id,
  })
}

class GraphDataService {
  private axios: AuthedAxiosRequests
  private readonly ROLODEX_BASE_URL: string

  constructor(axios: AuthedAxiosRequests, rolodexBaseUrl: string) {
    this.axios = axios
    this.ROLODEX_BASE_URL = rolodexBaseUrl
  }

  async executeGraphQuery(
    request: GraphDataRequest,
    options?: { signal?: AbortSignal }
  ): Promise<GraphDataResponse> {
    const requestBody = {
      query: request.query,
      params: request.params || {},
      organization_id: request.organization_id,
    }

    try {
      const response = await this.axios.post(
        `${this.ROLODEX_BASE_URL}/api/v2/neo4j/query`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          signal: options?.signal,
        }
      )

      if (!response.data) {
        return { results: [] }
      }

      const data = response.data
      if (Array.isArray(data)) {
        return { results: data }
      }

      return data as GraphDataResponse
    } catch (error) {
      console.error('Error executing graph query:', error)
      throw new Error(
        `Graph data API failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }
}

export const useGraphDataService = (organizationId: string) => {
  const { authorizedAxios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()

  const cacheRef = useRef<Map<string, CacheEntry>>(new Map())
  const inFlightRequestsRef = useRef<Map<string, InFlightRequest>>(new Map())
  const lastQueryKeyRef = useRef<string | null>(null)

  const service = useMemo(
    () => new GraphDataService(authorizedAxios, ROLODEX_URL),
    [authorizedAxios, ROLODEX_URL]
  )

  const pruneCache = useCallback(() => {
    const cache = cacheRef.current
    const now = Date.now()

    for (const [key, entry] of cache.entries()) {
      if (now - entry.timestamp > CACHE_TTL_MS) {
        cache.delete(key)
      }
    }

    if (cache.size > MAX_CACHE_SIZE) {
      const entries = Array.from(cache.entries()).sort(
        (a, b) => a[1].timestamp - b[1].timestamp
      )
      const toRemove = entries.slice(0, cache.size - MAX_CACHE_SIZE)
      for (const [key] of toRemove) {
        cache.delete(key)
      }
    }
  }, [])

  const executeGraphQuery = useCallback(
    async (
      request: Omit<GraphDataRequest, 'organization_id'>,
      options?: { signal?: AbortSignal; skipCache?: boolean }
    ): Promise<GraphDataResponse> => {
      const fullRequest: GraphDataRequest = {
        ...request,
        organization_id: organizationId,
      }
      const cacheKey = generateCacheKey(fullRequest)
      const cache = cacheRef.current
      const inFlightRequests = inFlightRequestsRef.current

      if (!options?.skipCache) {
        const cached = cache.get(cacheKey)
        if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
          return cached.response
        }
      }

      const existingInFlight = inFlightRequests.get(cacheKey)
      if (existingInFlight && !options?.skipCache) {
        return existingInFlight.promise
      }

      const lastKey = lastQueryKeyRef.current
      if (lastKey && lastKey !== cacheKey) {
        const previousRequest = inFlightRequests.get(lastKey)
        if (previousRequest) {
          previousRequest.abortController.abort()
          inFlightRequests.delete(lastKey)
        }
      }
      lastQueryKeyRef.current = cacheKey

      const abortController = new AbortController()

      const combinedSignal = options?.signal
        ? AbortSignal.any([options.signal, abortController.signal])
        : abortController.signal

      const requestPromise = (async (): Promise<GraphDataResponse> => {
        try {
          const response = await service.executeGraphQuery(fullRequest, {
            signal: combinedSignal,
          })

          cache.set(cacheKey, {
            response,
            timestamp: Date.now(),
          })

          pruneCache()

          return response
        } finally {
          inFlightRequests.delete(cacheKey)
        }
      })()

      inFlightRequests.set(cacheKey, {
        promise: requestPromise,
        abortController,
      })

      return requestPromise
    },
    [service, organizationId, pruneCache]
  )

  return {
    executeGraphQuery,
  }
}
